/**
 * SalesTeamsPage — mirrors Angular SalesTeamsComponent.
 * Three tabs: Team Structure, Company Assignments, Sales Lead Assignments.
 * Route: /sales-team (opens in new tab from user-management edit page).
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Loader2,
  ChevronsUpDown,
  Trash2,
  Plus,
  Users,
  Building2,
  UserCheck,
  Filter,
  X,
} from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { useUserRole } from '@/hooks/useUserRole'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SectionCard } from '@/components/ui/SectionCard'
import { EntityEditTabBar } from '@/components/ui/EntityEditTabBar'
import {
  primaryEntityActionButtonClass,
  deleteEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { getStates } from '@/lib/api/statesService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import tableStyles from '@/features/invitee/styles/invitesParity.module.css'
import styles from '../styles/salesTeams.module.css'
import type {
  CompanyListResponse,
  CompanyRow,
  SalesTeam,
  SalesTeamsTabKey,
  SalesUsersResponse,
  TeamRow,
} from '../types'

// ─── Component ────────────────────────────────────────────────────────────────

export function SalesTeamsPage() {
  const { isAdmin } = useUserRole()

  const [selectedTab, setSelectedTab] = useState<SalesTeamsTabKey>('teamstructure')
  const [processing, setProcessing] = useState(false)

  // ── Sales users & role titles ──────────────────────────────────────────
  const [salesResponse, setSalesResponse] = useState<SalesUsersResponse | null>(null)
  const [salesExecList, setSalesExecList] = useState<{ label: string; value: number }[]>([])
  const [salesManagerList, setSalesManagerList] = useState<{ label: string; value: number }[]>([])
  const [salesExecTitle, setSalesExecTitle] = useState('')
  const [accMgrTitle, setAccMgrTitle] = useState('')

  // ── Team structure ─────────────────────────────────────────────────────
  const [salesTeamList, setSalesTeamList] = useState<SalesTeam[]>([])
  const [teamRows, setTeamRows] = useState<TeamRow[]>([])

  // ── Company assignments ────────────────────────────────────────────────
  const [companyList, setCompanyList] = useState<CompanyRow[]>([])
  const [pageOptions, setPageOptions] = useState({ current: 1, last: 1, total: 0 })
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([])
  const [selectAllChecked, setSelectAllChecked] = useState(false)
  const [filters, setFilters] = useState({
    company_name: '', team_id: '', state: '', city: '', assigned: '', listed: '',
    team_name: '', page: 1, perPage: 15, sortBy: 'asc' as 'asc' | 'desc', orderBy: 'company_name',
  })
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (['page', 'perPage', 'sortBy', 'orderBy', 'team_name'].includes(key)) return false
    return value !== '' && value !== null && value !== undefined
  }).length
  const [states, setStates] = useState<{ code: string; name: string }[]>([])
  const [cities, setCities] = useState<Record<string, string[]>>({})
  const [cityOptions, setCityOptions] = useState<string[]>([])

  // ── Sales lead ─────────────────────────────────────────────────────────
  const [salesLeadId, setSalesLeadId] = useState('')

  // ── Confirm dialogs ────────────────────────────────────────────────────
  const [confirmState, setConfirmState] = useState<{
    open: boolean; title: string; message: string; onConfirm: () => void | Promise<void>
  }>({ open: false, title: '', message: '', onConfirm: () => {} })

  const showConfirm = (title: string, message: string, onConfirm: () => void | Promise<void>) => {
    setConfirmState({ open: true, title, message, onConfirm })
  }
  const closeConfirm = () => setConfirmState((p) => ({ ...p, open: false }))
  const confirmPrimaryBusyRef = useRef(false)

  const handleConfirmPrimary = () => {
    if (!confirmState.open || confirmPrimaryBusyRef.current) return
    confirmPrimaryBusyRef.current = true
    const run = confirmState.onConfirm
    closeConfirm()
    void Promise.resolve(run()).finally(() => {
      confirmPrimaryBusyRef.current = false
    })
  }

  // ══════════════════════════════════════════════════════════════════════
  // Data loading
  // ══════════════════════════════════════════════════════════════════════

  const loadSalesPersons = useCallback(async () => {
    setProcessing(true)
    try {
      const res = await apiClient.get<SalesUsersResponse>(API_ENDPOINTS.SALES_USERS)
      const data = res.data
      setSalesResponse(data)
      setSalesExecTitle(data.sales_exec_title || '')
      setAccMgrTitle(data.acc_mgr_title || '')
      setSalesLeadId(data.sales_lead ? String(data.sales_lead) : '')

      const execs = (data.sales || []).map((p) => ({ label: p.name, value: p.user_id }))
      const mgrs = (data.accounts || []).map((p) => ({ label: p.name, value: p.user_id }))
      setSalesExecList(execs)
      setSalesManagerList(mgrs)
    } catch (err) {
      showError(typeof err === 'object' && err !== null && 'message' in err ? String((err as { message: string }).message) : 'Failed to load sales users.')
    } finally {
      setProcessing(false)
    }
  }, [])

  const loadSalesTeam = useCallback(async () => {
    setProcessing(true)
    try {
      const res = await apiClient.get<SalesTeam[]>(API_ENDPOINTS.SALES_TEAM_CONFIG)
      const list = Array.isArray(res.data) ? res.data : []
      setSalesTeamList(list)
      setTeamRows(list.map((t) => ({
        ID: String(t.ID),
        TeamName: t.team_name || '',
        Relation_Mgr_Id: t.sales_manager?.[0]?.user_id ? String(t.sales_manager[0].user_id) : '',
        CustSuccMgr_Id: t.account_manager?.[0]?.user_id ? String(t.account_manager[0].user_id) : '',
      })))
    } catch {
      showError('Failed to load sales teams.')
    } finally {
      setProcessing(false)
    }
  }, [])

  const loadCompanyAssignList = useCallback(async (f?: typeof filters) => {
    const params = f ?? filters
    setProcessing(true)
    try {
      const query: Record<string, string | number> = {
        page: params.page, perPage: params.perPage, sortBy: params.sortBy, orderBy: params.orderBy,
      }
      if (params.company_name) query.company_name = params.company_name
      if (params.team_id) query.team_id = params.team_id
      if (params.state) query.state = params.state
      if (params.city) query.city = params.city
      if (params.assigned) query.assigned = params.assigned
      if (params.listed) query.listed = params.listed

      const res = await apiClient.get<CompanyListResponse>(API_ENDPOINTS.COMPANY_ASSIGNMENT_LIST, { params: query })
      setCompanyList(res.data.data || [])
      setPageOptions({ current: res.data.current, last: res.data.last, total: res.data.total })
      if (selectAllChecked) {
        setSelectedCompanies((res.data.data || []).map((c) => c.company_id))
      }
    } catch {
      setCompanyList([])
    } finally {
      setProcessing(false)
    }
  }, [filters, selectAllChecked])

  const loadStates = useCallback(async () => {
    try {
      // Angular parity: sales-teams pulls state list from global state loader, which uses GET /states filtered by zip_flag: '1'.
      const list = await getStates({ zip_flag: '1' })
      setStates(
        list
          .map((s) => ({
            code: String(s.state_code ?? s.abbreviation ?? '').trim(),
            name: String(s.state_name ?? s.name ?? '').trim(),
          }))
          .filter((s) => s.code && s.name),
      )
    } catch {
      setStates([])
    }
  }, [])

  const loadCities = useCallback(async () => {
    try {
      const res = await apiClient.get<Record<string, string[]>>(API_ENDPOINTS.COMPANY_CITY)
      setCities(res.data || {})
      const all: string[] = []
      Object.values(res.data || {}).forEach((arr) => arr.forEach((c) => { if (!all.includes(c)) all.push(c) }))
      setCityOptions(all.sort())
    } catch { /* ignore */ }
  }, [])

  // Initial load
  useEffect(() => {
    void loadSalesPersons().then(() => void loadSalesTeam())
    void loadStates()
    void loadCities()
    void loadCompanyAssignList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ══════════════════════════════════════════════════════════════════════
  // Team Structure handlers
  // ══════════════════════════════════════════════════════════════════════

  const addTeamRow = () => {
    setTeamRows((prev) => [...prev, { ID: '', TeamName: '', Relation_Mgr_Id: '', CustSuccMgr_Id: '' }])
  }

  const updateTeamRow = (index: number, field: keyof TeamRow, value: string) => {
    setTeamRows((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r))
  }

  const saveTeamRow = async (index: number) => {
    const row = teamRows[index]
    if (!row) return
    setProcessing(true)
    try {
      const payload = {
        ID: row.ID || undefined,
        TeamName: row.TeamName,
        Relation_Mgr_Id: row.Relation_Mgr_Id ? [row.Relation_Mgr_Id] : [],
        CustSuccMgr_Id: row.CustSuccMgr_Id ? [row.CustSuccMgr_Id] : [],
      }
      await apiClient.post(API_ENDPOINTS.SALES_TEAM_ADD_UPDATE(row.ID || undefined), payload)
      showSuccess('Sales Team Updated Successfully')
      void loadSalesTeam()
    } catch (err) {
      showError(typeof err === 'object' && err !== null && 'message' in err ? String((err as { message: string }).message) : 'Failed to save team.')
    } finally {
      setProcessing(false)
    }
  }

  const deleteTeamRow = (index: number) => {
    const row = teamRows[index]
    if (!row) return
    if (!row.ID) {
      setTeamRows((prev) => prev.filter((_, i) => i !== index))
      return
    }
    showConfirm('Remove Team', 'Do you want to remove Team?', async () => {
      setProcessing(true)
      try {
        await apiClient.post(API_ENDPOINTS.SALES_TEAM_DELETE(row.ID))
        showSuccess('Sales Team Deleted Successfully')
        void loadSalesTeam()
      } catch {
        showError('Failed to delete team.')
      } finally {
        setProcessing(false)
      }
    })
  }

  const updateRoleTitle = (roleKey: 'SALES_EXEC_TITLE' | 'ACC_MGR_TITLE') => {
    const value = roleKey === 'SALES_EXEC_TITLE' ? salesExecTitle : accMgrTitle
    setConfirmState({
      open: true,
      title: 'Update Role Name',
      message: 'Do you want to update role name?',
      onConfirm: async () => {
        setProcessing(true)
        try {
          await apiClient.post(API_ENDPOINTS.SALES_ROLE_TITLE_UPDATE, { name: roleKey, value })
          showSuccess('Role Name Updated Successfully')
        } catch {
          showError('Failed to update role name.')
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Angular: on cancel, revert the title input back to original value
  const handleRoleTitleCancel = useCallback(() => {
    closeConfirm()
    // Reload to revert the input values
    void loadSalesPersons()
  }, [loadSalesPersons])

  // ══════════════════════════════════════════════════════════════════════
  // Company Assignments handlers
  // ══════════════════════════════════════════════════════════════════════

  const applyFilters = (overrides?: Partial<typeof filters>) => {
    const next = { ...filters, ...overrides, page: 1 }
    setFilters(next)
    void loadCompanyAssignList(next)
  }

  const resetFilters = () => {
    const defaults = {
      company_name: '', team_id: '', state: '', city: '', assigned: '', listed: '',
      team_name: '', page: 1, perPage: 15, sortBy: 'asc' as const, orderBy: 'company_name',
    }
    setFilters(defaults)
    setCityOptions(() => {
      const all: string[] = []
      Object.values(cities).forEach((arr) => arr.forEach((c) => { if (!all.includes(c)) all.push(c) }))
      return all.sort()
    })
    void loadCompanyAssignList(defaults)
  }

  const handleSort = (col: string) => {
    const next = {
      ...filters,
      orderBy: col,
      sortBy: (filters.sortBy === 'asc' ? 'desc' : 'asc') as 'asc' | 'desc',
    }
    setFilters(next)
    void loadCompanyAssignList(next)
  }

  const handleStateChange = (stateCode: string) => {
    setFilters((p) => ({ ...p, state: stateCode, city: '' }))
    if (stateCode && cities[stateCode]) {
      setCityOptions(cities[stateCode])
    } else {
      const all: string[] = []
      Object.values(cities).forEach((arr) => arr.forEach((c) => { if (!all.includes(c)) all.push(c) }))
      setCityOptions(all.sort())
    }
  }

  const toggleSelectAll = (checked: boolean) => {
    setSelectAllChecked(checked)
    if (checked) {
      setSelectedCompanies(companyList.map((c) => c.company_id))
    } else {
      setSelectedCompanies([])
    }
  }

  const toggleCompany = (companyId: number, checked: boolean) => {
    setSelectAllChecked(false)
    setSelectedCompanies((prev) =>
      checked ? [...prev, companyId] : prev.filter((id) => id !== companyId)
    )
  }

  const changePage = (page: number) => {
    const p = Math.max(1, Math.min(page, pageOptions.last))
    const next = { ...filters, page: p }
    setFilters(next)
    void loadCompanyAssignList(next)
  }

  const changePerPage = (pp: number) => {
    const next = { ...filters, perPage: pp, page: 1 }
    setFilters(next)
    void loadCompanyAssignList(next)
  }

  const allSalesIdMap = salesTeamList.reduce<Record<string, string>>((m, t) => {
    m[String(t.ID)] = t.team_name; return m
  }, {})

  const assignTeam = () => {
    const teamName = allSalesIdMap[filters.team_name] || ''
    const count = selectAllChecked ? pageOptions.total : selectedCompanies.length
    showConfirm('Team Assignment', `You are about to assign team ${teamName} to ${count} companies. Do you want to proceed?`, async () => {
      setProcessing(true)
      try {
        const payload: Record<string, unknown> = {
          sales_team_id: filters.team_name,
          company_id: selectedCompanies,
        }
        if (selectAllChecked) {
          const filterData: Record<string, unknown> = {}
          Object.entries(filters).forEach(([k, v]) => {
            if (k === 'team_name') return
            if (v !== '' && v !== null && v !== undefined) {
              filterData[k] = typeof v === 'string' ? v.trim() : v
            }
          })
          payload.select_all = true
          payload.filter_data = filterData
        }
        await apiClient.post(API_ENDPOINTS.SALES_TEAM_ASSIGN, payload)
        showSuccess('Team Assign Successfully!')
        setSelectedCompanies([])
        setSelectAllChecked(false)
        void loadCompanyAssignList()
      } catch {
        showError('Failed to assign team.')
      } finally {
        setProcessing(false)
      }
    })
  }

  // ══════════════════════════════════════════════════════════════════════
  // Sales Lead handler
  // ══════════════════════════════════════════════════════════════════════

  const assignSalesLead = () => {
    showConfirm('Sales Lead Assignment', 'You are about to assign Sales Lead. Do you want to proceed?', async () => {
      setProcessing(true)
      try {
        await apiClient.post(API_ENDPOINTS.SALES_LEAD_UPDATE(salesLeadId))
        showSuccess('Sales Lead Assign Successfully!')
      } catch {
        showError('Failed to assign sales lead.')
      } finally {
        setProcessing(false)
      }
    })
  }

  // ══════════════════════════════════════════════════════════════════════
  // Pagination helper — generates page number buttons (matches Angular app-paginator)
  // ══════════════════════════════════════════════════════════════════════

  const renderPageButtons = () => {
    if (pageOptions.last <= 1) return null
    const current = pageOptions.current
    const last = pageOptions.last
    const pages: number[] = []
    const limit = 5
    let start = Math.max(1, current - Math.floor(limit / 2))
    const end = Math.min(last, start + limit - 1)
    if (end - start + 1 < limit) start = Math.max(1, end - limit + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className={styles.pageBtnGroup}>
        <button type="button" className={styles.pageBtn} onClick={() => changePage(1)} disabled={current === 1}>&laquo;</button>
        <button type="button" className={styles.pageBtn} onClick={() => changePage(current - 1)} disabled={current === 1}>&lsaquo;</button>
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => changePage(p)}
            className={cn(styles.pageBtn, p === current && styles.pageBtnActive)}
          >
            {p}
          </button>
        ))}
        <button type="button" className={styles.pageBtn} onClick={() => changePage(current + 1)} disabled={current === last}>&rsaquo;</button>
        <button type="button" className={styles.pageBtn} onClick={() => changePage(last)} disabled={current === last}>&raquo;</button>
      </div>
    )
  }

  // ══════════════════════════════════════════════════════════════════════
  // Sort icon helper
  // ══════════════════════════════════════════════════════════════════════

  const SortIcon = ({ col }: { col: string }) => {
    const isActive = filters.orderBy === col
    return (
      <span
        aria-hidden="true"
        className={cn(tableStyles.sortIcon, isActive && tableStyles.sortIconActive)}
      >
        {isActive ? (filters.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
      </span>
    )
  }

  // ══════════════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════════════

  const tabs: { key: SalesTeamsTabKey; label: string; adminOnly?: boolean }[] = [
    { key: 'teamstructure', label: 'Team Structure' },
    { key: 'companyassignments', label: 'Company Assignments' },
    { key: 'leadassignments', label: 'Sales Lead Assignments', adminOnly: true },
  ]

  return (
    <div className={styles.page}>
      <PageHeader title="SALES TEAM CONFIG" />

      <div className={styles.content}>
        <div className={styles.contentInner}>
          {/* Tabs */}
          <EntityEditTabBar
            navTestId="sales-teams-tab-nav"
            tabs={tabs.filter((tab) => !(tab.adminOnly && !isAdmin)).map((tab) => ({
              id: tab.key,
              label: tab.label,
              testId: `sales-teams-tab-${tab.key}`,
            }))}
            activeTab={selectedTab}
            onSelect={(tabId) => setSelectedTab(tabId as SalesTeamsTabKey)}
          />

          {/* ── Team Structure Tab ──────────────────────────────────── */}
          {selectedTab === 'teamstructure' && (
            <SectionCard
              icon={Users}
              title="Team Structure"
              headerActions={
                <button
                  type="button"
                  onClick={addTeamRow}
                  className={cn(primaryEntityActionButtonClass, 'gap-2')}
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              }
            >
              {/* Role title fields */}
              <div className={styles.roleTitleGrid}>
                <div>
                  <label className={styles.formLabel}>Team Name</label>
                  <p className={styles.roleTitleSpacer}>&nbsp;</p>
                </div>
                <div>
                  <label className={styles.formLabel}>Sales Exec:</label>
                  <input
                    type="text"
                    value={salesExecTitle}
                    onChange={(e) => setSalesExecTitle(e.target.value)}
                    onBlur={() => { if (salesExecTitle !== (salesResponse?.sales_exec_title || '')) updateRoleTitle('SALES_EXEC_TITLE') }}
                    className={styles.formInput}
                  />
                </div>
                <div>
                  <label className={styles.formLabel}>Acct Mgr:</label>
                  <input
                    type="text"
                    value={accMgrTitle}
                    onChange={(e) => setAccMgrTitle(e.target.value)}
                    onBlur={() => { if (accMgrTitle !== (salesResponse?.acc_mgr_title || '')) updateRoleTitle('ACC_MGR_TITLE') }}
                    className={styles.formInput}
                  />
                </div>
              </div>

              {/* Team rows */}
              <div className={styles.teamRowsContainer}>
                {teamRows.map((row, i) => (
                  <div key={row.ID || `new-${i}`} className={styles.teamRowCard}>
                    <div>
                      <input
                        type="text"
                        placeholder="Team Name"
                        value={row.TeamName}
                        onChange={(e) => updateTeamRow(i, 'TeamName', e.target.value)}
                        className={styles.formInput}
                      />
                    </div>
                    <div>
                      <select
                        value={row.Relation_Mgr_Id}
                        onChange={(e) => updateTeamRow(i, 'Relation_Mgr_Id', e.target.value)}
                        className={styles.formSelect}
                      >
                        <option value="">Select User</option>
                        {salesExecList.map((u) => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select
                        value={row.CustSuccMgr_Id}
                        onChange={(e) => updateTeamRow(i, 'CustSuccMgr_Id', e.target.value)}
                        className={styles.formSelect}
                      >
                        <option value="">Select User</option>
                        {salesManagerList.map((u) => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.teamRowActions}>
                      <button
                        type="button"
                        onClick={() => void saveTeamRow(i)}
                        disabled={processing}
                        className={primaryEntityActionButtonClass}
                      >
                        {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteTeamRow(i)}
                        disabled={processing}
                        className={deleteEntityActionButtonClass}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {teamRows.length === 0 && !processing && (
                <p className={styles.emptyText}>No teams found. Click Add to create one.</p>
              )}

              {processing && (
                <div className={styles.loaderWrap}>
                  <Loader2 className={cn('h-5 w-5 animate-spin', styles.loaderIcon)} />
                </div>
              )}
            </SectionCard>
          )}

          {/* ── Company Assignments Tab ─────────────────────────────── */}
          {selectedTab === 'companyassignments' && (
            <SectionCard icon={Building2} title="Company Assignments">
              {/* Assign Team & Filters row */}
              <div className={styles.assignRowWrapper}>
                {/* Left: Assign Team */}
                {salesTeamList.length > 0 ? (
                  <div className={styles.assignTeamControls}>
                    <div>
                      <label className={styles.formLabel}>Select Team to Assign</label>
                      <select
                        value={filters.team_name}
                        onChange={(e) => setFilters((p) => ({ ...p, team_name: e.target.value }))}
                        className={cn(styles.formSelect, styles.selectWide)}
                      >
                        <option value="">All</option>
                        {salesTeamList.map((t) => <option key={t.ID} value={t.ID}>{t.team_name}</option>)}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={assignTeam}
                      disabled={!filters.team_name || selectedCompanies.length === 0 || processing}
                      className={primaryEntityActionButtonClass}
                    >
                      Assign Team
                    </button>
                  </div>
                ) : (
                  <div></div> /* Empty div to push filters to the right if no teams exist */
                )}

                {/* Right: Filters Toggle */}
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(true)}
                  className={cn(
                    styles.filterToggleBtn,
                    activeFiltersCount > 0 && styles.filterToggleBtnActive
                  )}
                >
                  <Filter className="h-4 w-4 shrink-0" aria-hidden />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className={styles.filterBadge}>
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Pagination top */}
              <div className={styles.paginationRow}>
                <div className={styles.paginationLeft}>
                  {pageOptions.last > 1 && (
                    <>
                      <span>Page</span>
                      <input
                        type="number"
                        className={styles.pageInput}
                        value={pageOptions.current}
                        onChange={(e) => changePage(Number(e.target.value))}
                        min={1}
                        max={pageOptions.last}
                      />
                      <span>of {pageOptions.last}</span>
                    </>
                  )}
                  {pageOptions.total > 0 && <span className={styles.recordsInfo}>({pageOptions.total} Records)</span>}
                </div>
                {renderPageButtons()}
              </div>

              {/* Table — uses shared listingTable styles from invitesParity.module.css */}
              {companyList.length > 0 ? (
                <div className={tableStyles.tableCard}>
                  <div className={cn(tableStyles.tableResponsiveScroll, 'touch-scroll')}>
                    <table
                      className={cn(
                        tableStyles.listingTable,
                        tableStyles.propertyListingTable,
                        styles.companyTable
                      )}
                    >
                      <colgroup>
                        <col style={{ width: '3%' }} />
                        <col style={{ width: '23%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '16%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>
                            <label className={styles.selectAllLabel}>
                              <input
                                type="checkbox"
                                checked={selectAllChecked || selectedCompanies.length === companyList.length}
                                onChange={(e) => toggleSelectAll(e.target.checked)}
                                className={styles.checkbox}
                              />
                              <span className={styles.selectAllText}>Select All</span>
                            </label>
                          </th>
                          {[
                            { key: 'company_name', label: 'Company Name' },
                            { key: 'listed', label: 'Liberty Listed' },
                            { key: 'company_city', label: 'City' },
                            { key: 'state', label: 'State' },
                            { key: 'company_zipcode', label: 'Zip' },
                            { key: 'TeamName', label: 'Team assigned' },
                          ].map((col) => (
                            <th
                              key={col.key}
                              onClick={() => handleSort(col.key)}
                            >
                              <button
                                type="button"
                                className={cn(
                                  tableStyles.sortHeaderButton,
                                  tableStyles.propertySortHeaderButton
                                )}
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handleSort(col.key)
                                }}
                              >
                                <span className={tableStyles.sortHeaderLabel}>{col.label}</span>
                                <SortIcon col={col.key} />
                              </button>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {companyList.map((c) => (
                          <tr key={c.company_id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedCompanies.includes(c.company_id)}
                                onChange={(e) => toggleCompany(c.company_id, e.target.checked)}
                                className={styles.checkbox}
                              />
                            </td>
                            <td>{c.company_name}</td>
                            <td>{c.listed === '1' ? 'Active' : 'Not Active'}</td>
                            <td>{c.company_city}</td>
                            <td>{c.state}</td>
                            <td>{c.company_zipcode}</td>
                            <td>{c.TeamName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className={styles.emptyText}>No companies found.</p>
              )}

              {/* Pagination bottom */}
              {pageOptions.last > 1 && (
                <div className={styles.paginationRow}>
                  <div className={styles.paginationLeft}>
                    <span>Page</span>
                    <input
                      type="number"
                      className={styles.pageInput}
                      value={pageOptions.current}
                      onChange={(e) => changePage(Number(e.target.value))}
                      min={1}
                      max={pageOptions.last}
                    />
                    <span>of {pageOptions.last}</span>
                    {pageOptions.total > 0 && <span className={styles.recordsInfo}>({pageOptions.total} Records)</span>}
                  </div>
                  <div className={styles.paginationRight}>
                    <div className={styles.paginationPerPage}>
                      <label>Per Page:</label>
                      <select
                        value={filters.perPage}
                        onChange={(e) => changePerPage(Number(e.target.value))}
                        className={styles.perPageSelect}
                      >
                        <option value={15}>15</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    {renderPageButtons()}
                  </div>
                </div>
              )}

              {processing && (
                <div className={styles.loaderWrap}>
                  <Loader2 className={cn('h-5 w-5 animate-spin', styles.loaderIcon)} />
                </div>
              )}
            </SectionCard>
          )}

          {/* ── Sales Lead Assignments Tab ──────────────────────────── */}
          {selectedTab === 'leadassignments' && isAdmin && (
            <SectionCard icon={UserCheck} title="Sales Lead Assignments">
              {salesResponse?.liberty_sales && salesResponse.liberty_sales.length > 0 ? (
                <div className={styles.salesLeadRow}>
                  <div>
                    <label className={styles.formLabel}>Select User to Assign</label>
                    <select
                      value={salesLeadId}
                      onChange={(e) => setSalesLeadId(e.target.value)}
                      className={cn(styles.formSelect, styles.selectWider)}
                    >
                      <option value="">Select User</option>
                      {salesResponse.liberty_sales.map((u) => (
                        <option key={u.user_id} value={u.user_id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={assignSalesLead}
                    disabled={!salesLeadId || processing}
                    className={primaryEntityActionButtonClass}
                  >
                    {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                    Assign Sales Lead
                  </button>
                </div>
              ) : (
                <p className={styles.emptyText}>No liberty sales users available.</p>
              )}
            </SectionCard>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        primaryLabel="Yes"
        onPrimary={handleConfirmPrimary}
        secondaryLabel="No"
        onSecondary={confirmState.title === 'Update Role Name' ? handleRoleTitleCancel : closeConfirm}
        onDismiss={confirmState.title === 'Update Role Name' ? handleRoleTitleCancel : closeConfirm}
        primaryFirst
      />

      {/* Filters Drawer */}
      {isFilterOpen && (
        <>
          <div
            className={styles.drawerOverlay}
            onClick={() => setIsFilterOpen(false)}
            aria-hidden
          />
          <div className={styles.drawerPanel}>
            <div className={styles.drawerHeader}>
              <div className={styles.drawerHeaderTitle}>
                <Filter className="h-5 w-5 shrink-0 text-[rgb(21_151_255)]" aria-hidden />
                <h2>Filter Companies</h2>
              </div>
              <button
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className={styles.drawerCloseBtn}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className={styles.drawerContentWrap}>
              <div className={styles.drawerContentInner}>
                <div>
                  <label className={styles.formLabel}>Company Name</label>
                  <input
                    type="text"
                    placeholder="Company Name"
                    value={filters.company_name}
                    onChange={(e) => setFilters((p) => ({ ...p, company_name: e.target.value }))}
                    className={cn(styles.formInput, filters.company_name ? styles.formSelectFilled : '')}
                  />
                </div>
                {salesTeamList.length > 0 && (
                  <div>
                    <label className={styles.formLabel}>Select Team</label>
                    <select
                      value={filters.team_id}
                      onChange={(e) => setFilters((p) => ({ ...p, team_id: e.target.value }))}
                      className={cn(styles.formSelect, filters.team_id ? styles.formSelectFilled : '')}
                    >
                      <option value="">Select Team</option>
                      {salesTeamList.map((t) => <option key={t.ID} value={t.ID}>{t.team_name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className={styles.formLabel}>Select State</label>
                  <select
                    value={filters.state}
                    onChange={(e) => handleStateChange(e.target.value)}
                    className={cn(styles.formSelect, filters.state ? styles.formSelectFilled : '')}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={styles.formLabel}>Select City</label>
                  <select
                    value={filters.city}
                    onChange={(e) => setFilters((p) => ({ ...p, city: e.target.value }))}
                    className={cn(styles.formSelect, filters.city ? styles.formSelectFilled : '')}
                  >
                    <option value="">All City</option>
                    {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={styles.formLabel}>Assignment Status</label>
                  <select
                    value={filters.assigned}
                    onChange={(e) => setFilters((p) => ({ ...p, assigned: e.target.value }))}
                    className={cn(styles.formSelect, filters.assigned ? styles.formSelectFilled : '')}
                  >
                    <option value="">All Team</option>
                    <option value="1">Assigned</option>
                    <option value="0">Not Assigned</option>
                  </select>
                </div>
                <div>
                  <label className={styles.formLabel}>Liberty Listed</label>
                  <select
                    value={filters.listed}
                    onChange={(e) => setFilters((p) => ({ ...p, listed: e.target.value }))}
                    className={cn(styles.formSelect, filters.listed ? styles.formSelectFilled : '')}
                  >
                    <option value="">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Not Active</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.drawerFooter}>
              <button
                type="button"
                onClick={() => { resetFilters(); setIsFilterOpen(false); }}
                className="filter-drawer-reset-all"
              >
                Reset All
              </button>
              <button
                type="button"
                onClick={() => { applyFilters(); setIsFilterOpen(false); }}
                className={styles.drawerDoneBtn}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
